/**
 * Display metadata for the pricing / paywall UI. The authoritative *limits* come
 * from the backend (GET /me → entitlements.all_tiers); this file only carries
 * marketing copy, ordering, prices and styling.
 *
 * Single-payer model: limits belong to the baby owner's subscription. Tiers are
 * free / premium (Family) / ultra (Village). A one-time "Full Journey" pass
 * grants a tier permanently — see LIFETIME_OPTIONS — and is represented on the
 * subscription as that tier with plan_type='lifetime'.
 *
 * Paddle is Merchant of Record and shows the buyer their localised, tax-inclusive
 * price at checkout, so these strings are indicative headline prices only.
 */
import type { SubscriptionTier, TierLimits } from './dto';

export const TIER_ORDER: SubscriptionTier[] = ['free', 'premium', 'ultra'];

/** The two monthly tiers the paywall lets you toggle between. */
export const MONTHLY_TIERS: Exclude<SubscriptionTier, 'free'>[] = ['premium', 'ultra'];

/** Upgrade ranking on the tier ladder. */
const RANK: Record<SubscriptionTier, number> = { free: 0, premium: 1, ultra: 2 };

export interface TierMeta {
  nameKey: string;
  nameFallback: string;
  priceUsd: string;
  priceEur: string;
  amountUsd: number;
  amountEur: number;
  cadence: 'mo' | '';
  taglineKey: string;
  taglineFallback: string;
  highlight: boolean;
}

export const TIER_META: Record<SubscriptionTier, TierMeta> = {
  free: {
    nameKey: 'billing.tier.free', nameFallback: 'Single Tracker',
    priceUsd: '$0', priceEur: '€0', amountUsd: 0, amountEur: 0, cadence: '',
    taglineKey: 'billing.tagline.free', taglineFallback: 'Track one baby, forever free.',
    highlight: false,
  },
  premium: {
    nameKey: 'billing.tier.premium', nameFallback: 'Family',
    priceUsd: '$1.99', priceEur: '€1,99', amountUsd: 1.99, amountEur: 1.99, cadence: 'mo',
    taglineKey: 'billing.tagline.premium', taglineFallback: 'One plan covers the whole family — only the owner pays.',
    highlight: true,
  },
  ultra: {
    nameKey: 'billing.tier.ultra', nameFallback: 'Village',
    priceUsd: '$3.99', priceEur: '€3,99', amountUsd: 3.99, amountEur: 3.99, cadence: 'mo',
    taglineKey: 'billing.tagline.ultra', taglineFallback: 'It takes a village — room for the whole extended family.',
    highlight: false,
  },
};

/**
 * One-time "Full Journey" passes: pay once, keep the tier forever. Each grants
 * the matching monthly tier's limits. The Family pass can later be upgraded to
 * the Village pass for the price difference.
 */
export interface LifetimeOption {
  tier: Exclude<SubscriptionTier, 'free'>;
  nameKey: string;
  nameFallback: string;
  priceUsd: string;
  priceEur: string;
  amountUsd: number;
  amountEur: number;
}

export const LIFETIME_OPTIONS: LifetimeOption[] = [
  {
    tier: 'premium',
    nameKey: 'billing.fj.family', nameFallback: 'Family Full Journey',
    priceUsd: '$29.99', priceEur: '€29,99', amountUsd: 29.99, amountEur: 29.99,
  },
  {
    tier: 'ultra',
    nameKey: 'billing.fj.village', nameFallback: 'Village Full Journey',
    priceUsd: '$59.99', priceEur: '€59,99', amountUsd: 59.99, amountEur: 59.99,
  },
];

/** Family→Village one-time upgrade price. */
export const FJ_UPGRADE = { priceUsd: '$30', priceEur: '€30', amountUsd: 30, amountEur: 30 };

/** The one-time pass buys this many months of the tier (then falls back to free). */
export const ONE_TIME_MONTHS = 24;

/** The one-time option for a given tier (undefined for free). */
export function oneTimeFor(tier: SubscriptionTier): LifetimeOption | undefined {
  return LIFETIME_OPTIONS.find((o) => o.tier === tier);
}

/** Euro for European locales, dollar otherwise (headline only). */
const EUR_LOCALES = new Set(['de', 'fr', 'es', 'pt', 'it', 'nl', 'uk']);

export function isEuro(locale: string): boolean {
  return EUR_LOCALES.has((locale || 'en').slice(0, 2).toLowerCase());
}

export function tierPrice(tier: SubscriptionTier, locale: string): string {
  const meta = TIER_META[tier];
  return isEuro(locale) ? meta.priceEur : meta.priceUsd;
}

export function tierAmount(tier: SubscriptionTier, locale: string): number {
  const meta = TIER_META[tier];
  return isEuro(locale) ? meta.amountEur : meta.amountUsd;
}

export function lifetimePrice(opt: LifetimeOption, locale: string): string {
  return isEuro(locale) ? opt.priceEur : opt.priceUsd;
}

export function lifetimeAmount(opt: LifetimeOption, locale: string): number {
  return isEuro(locale) ? opt.amountEur : opt.amountUsd;
}

export function currencySymbol(locale: string): string {
  return isEuro(locale) ? '€' : '$';
}

export function isUpgrade(current: SubscriptionTier, target: SubscriptionTier): boolean {
  return RANK[target] > RANK[current];
}

/** The plan shown as the "before" column when comparing against `tier`. */
export function lowerTierFor(tier: SubscriptionTier): SubscriptionTier {
  return tier === 'ultra' ? 'premium' : 'free';
}

/**
 * Savings of the one-time (24-month) pass vs paying its tier monthly for the
 * same 24 months: absolute amount saved and the discount percentage.
 */
export function oneTimeSavings(opt: LifetimeOption, locale: string): { months: number; save: number; pct: number } {
  const monthly = tierAmount(opt.tier, locale);
  const total   = monthly * ONE_TIME_MONTHS;
  const oneTime = lifetimeAmount(opt, locale);
  const save    = Math.max(0, Math.round((total - oneTime) * 100) / 100);
  const pct     = total > 0 ? Math.round((save / total) * 100) : 0;
  return { months: ONE_TIME_MONTHS, save, pct };
}

/** Feature rows shown in the comparison table, in order. */
export const FEATURE_ROWS: { key: keyof TierLimits; labelKey: string; labelFallback: string }[] = [
  { key: 'babies',             labelKey: 'billing.feat.babies',   labelFallback: 'Babies (you own)' },
  { key: 'members_per_baby',   labelKey: 'billing.feat.sharing',  labelFallback: 'Share with' },
  { key: 'images',             labelKey: 'billing.feat.images',   labelFallback: 'Photos' },
  { key: 'videos',             labelKey: 'billing.feat.videos',   labelFallback: 'Videos' },
  { key: 'activity_ai_weekly', labelKey: 'billing.feat.ai',       labelFallback: 'AI activity ideas / week' },
];

/** Features every plan includes — the "Always included" block. */
export const ALWAYS_INCLUDED: { key: string; fallback: string }[] = [
  { key: 'billing.incl.tracking',   fallback: 'All milestone, leap & growth tracking' },
  { key: 'billing.incl.activities', fallback: 'Daily activity suggestions' },
  { key: 'billing.incl.onepayer',   fallback: 'One plan covers everyone you share with' },
  { key: 'billing.incl.private',    fallback: 'Private, encrypted photo storage' },
];
